import React, {useEffect, useState} from "react";
import { useForm, FormProvider } from "react-hook-form";
import {useTranslation} from "react-i18next";
import get from "lodash/get";

import Button from "@mui/material/Button";
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

import IncotermInput from '../invoiceFields/incotermsInput'
import LocationInput from '../invoiceFields/locationInput'
import DateInput from '../invoiceFields/dateInput'

import {useGetCalcIdFromRoute} from "../../../../../../../../../../../utils/hooks";
import {
  useCreateInvoiceMutation,
  useCreateProductMutation,
  useGetInvoices,
  useInvoicesByOrderIdQuery,
  useLazyProductsDownloadExcelByInvoiceIdQuery, useLazySummaryByOrderIdQuery,
  useProductsByInvoiceIdQuery,
  useSummaryByOrderIdQuery,
  useUpdateInvoiceMutation,
} from "../../../../../../../../../../../api/calc";
import Notification, {NOTIFICATION_TYPE} from "../../../../../../../../../../common/notification";
import {COLORS, ROUTES} from "../../../../../../../../../../../config/constants";


import AddForm from "../products/components/add_form";
import {PackageForm} from "../products/components/add_form/helpers";

import './editInvoice.scss'
import Supplier from "../supplier";
import {useCreateSupplierMutation} from "../../../../../../../../../../../api/dictionary";
import {useDemoInvoiceForm} from 'components/cabinet/demo/steps/packaging/use-packaging';
import {isCalculationViewModeError} from 'utils/helpers';
import {LinkPopover} from '@vedmarket/ui-kit';
import { formatCopy } from 'utils/typography';

export const enum INVOICE_FORM {
  DESCRIPTION = 'description',
  INVOICE_NUM = 'invoice_num',
  INVOICE_DATE = 'invoice_date',
  INCOTERMS = 'incoterms',
  LOCATION = 'location',
  SUPPLIER = 'supplier',
}

export type InvoiceForm = {
  [INVOICE_FORM.DESCRIPTION]: string
  [INVOICE_FORM.INVOICE_NUM]: string
  [INVOICE_FORM.INVOICE_DATE]: string
  [INVOICE_FORM.INCOTERMS]: string
  [INVOICE_FORM.LOCATION]: string
  [INVOICE_FORM.SUPPLIER]?: number | string
}

type Props = {
  handleSuccess: () => void
  noBorder?: boolean
  hasCancelButton?: boolean
  isFirstInvoice?: boolean
  currentInvoice?: InvoiceParamsOutput
  onClose?(): void
  onCloseInvoiceForm?(): void
}

type OptionData = {
  label: string
}

type LocationItemDefaultCity = LocationItem & OptionData;

const EditInvoice = ( { handleSuccess, currentInvoice, onCloseInvoiceForm, isFirstInvoice, onClose } : Props ) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute();

  const [ defaultCity, setDefaultCity ] = useState<LocationItemDefaultCity>()

  const defaultCityName = currentInvoice?.[INVOICE_FORM.LOCATION]

  useEffect(() => {
    if (defaultCityName){
      setDefaultCity({...defaultCityName, label: `${defaultCityName.name}, ${defaultCityName.country.name}`})
    }
  }, [ defaultCityName ])

  const [ showErrorNotificationProduct, setShowErrorNotificationProduct ] = useState<boolean | undefined>(false);
  const [ showErrorNotificationInvoice, setShowErrorNotificationInvoice ] = useState<boolean | undefined>(false);

  const defaultValues = {
    [INVOICE_FORM.DESCRIPTION]: currentInvoice?.[INVOICE_FORM.DESCRIPTION] || '',
    [INVOICE_FORM.INVOICE_NUM]: currentInvoice?.[INVOICE_FORM.INVOICE_NUM] || '',
    [INVOICE_FORM.INVOICE_DATE]: currentInvoice?.[INVOICE_FORM.INVOICE_DATE] || '',
    [INVOICE_FORM.INCOTERMS]: currentInvoice?.[INVOICE_FORM.INCOTERMS] || '',
    [INVOICE_FORM.LOCATION]: currentInvoice?.[INVOICE_FORM.LOCATION]?.id || '',
    [INVOICE_FORM.SUPPLIER]: currentInvoice?.[INVOICE_FORM.SUPPLIER]?.id || '',
  };

  const methods = useForm<InvoiceForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultValues,
  })

  const { register, reset, getValues, watch, control, handleSubmit, formState: { errors } } = methods

  const [ createInvoice ] = useCreateInvoiceMutation()

  const [ updateInvoice ] = useUpdateInvoiceMutation()

  const [ createProduct ] = useCreateProductMutation()

  const { refetch: refetchSummary } = useSummaryByOrderIdQuery(orderId, {skip: !orderId})
  const { refetch: refetchInvoices } = useInvoicesByOrderIdQuery(orderId, {skip: !orderId})
  const { refetch: refetchProductsByInvoiceId } = useProductsByInvoiceIdQuery(currentInvoice?.id || 0, {skip: !currentInvoice?.id})

  const onSubmit = handleSubmit(async values => {

    if (!currentInvoice){
      setShowErrorNotificationInvoice(true);
      return;
    }

    const result = await updateInvoice({
      orderId,
      invoiceId: currentInvoice?.id,
      ...values
    })

    if (isCalculationViewModeError(result)) {
      return;
    }

    const invoiceId = get(result, 'data.id', '')

    if (!invoiceId) {
      setShowErrorNotificationInvoice(true);
      return
    }
    setShowErrorNotificationInvoice(false);
    handleSuccess()
    reset()
  })

  const createNewInvoice = async (data: PackageForm) => {

    const values = getValues();

    const result = await createInvoice({
      id: orderId,
      // @ts-ignore
      supplier: values.supplier || null,
      // @ts-ignore
      ...values,
    })

    const invoiceId = get(result, 'data.id', '')

    if (!invoiceId) {
      return
    }

    const productResult = await createProduct({ invoiceId: invoiceId, ...data})

    // @ts-ignore
    const errorData = productResult?.error?.data
    const productId = get(productResult, 'data.id', '')

    if (!productId || errorData?.non_field_errors) {
      setShowErrorNotificationProduct(errorData?.non_field_errors?.[0] || true);
      return
    }
    setShowErrorNotificationProduct(false);
    handleSuccess()
    refetchSummary()
    onCloseInvoiceForm && onCloseInvoiceForm()
    reset()
  }


  const watchAllFields = watch()

  const saveButtonIsActive = Boolean(watchAllFields[INVOICE_FORM.DESCRIPTION])
  && Boolean(watchAllFields[INVOICE_FORM.INVOICE_NUM])
  && Boolean(watchAllFields[INVOICE_FORM.INVOICE_DATE])
  && watchAllFields[INVOICE_FORM.INVOICE_DATE] !== 'Invalid date'
  && Boolean(watchAllFields[INVOICE_FORM.INCOTERMS])
  && Boolean(watchAllFields[INVOICE_FORM.LOCATION])

  const demoPlayback = useDemoInvoiceForm(saveButtonIsActive)

  const handleSuccessAddedProduct = () => {
    refetchInvoices()
    refetchProductsByInvoiceId();
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        {!currentInvoice && <Notification
          type={NOTIFICATION_TYPE.INFO}
          title={formatCopy(t('calc__text__main__invoiceDetails'))}
          text={(
            <Typography variant='body2' color='inherit'>
              {formatCopy(t('calc__text__main__invoiceDetailsNotification'))}
            </Typography>
          )}
          sx={{ marginBottom: 3}}
        />}
        <div className='invoice-form'>
          <div className='invoice-field invoice-field_wide'>
            <TextField
              {...register(INVOICE_FORM.DESCRIPTION, { required: true }) }
              error={Boolean(errors[INVOICE_FORM.DESCRIPTION])}
              helperText={errors[INVOICE_FORM.DESCRIPTION]?.message}
              variant='outlined'
              label={t('calc__text__main__descriptionOfTheGoods')}
              className={`invoice-form__${INVOICE_FORM.DESCRIPTION}`}
              InputLabelProps={{ shrink: true }}
            />
            <LinkPopover
              linkText={t('calc__help__invoiceDescriptionLink')}
              text={formatCopy(t('calc__help__invoiceDescription'))}
            />
          </div>
          <Supplier />
          <div className='invoice-field'>
            <TextField
              {...register(INVOICE_FORM.INVOICE_NUM, { required: true}) }
              error={Boolean(errors[INVOICE_FORM.INVOICE_NUM])}
              helperText={errors[INVOICE_FORM.INVOICE_NUM]?.message}
              variant='outlined'
              label={t('calc__text__main__numOfInvoice')}
              className={`invoice-form__${INVOICE_FORM.INVOICE_NUM}`}
              InputLabelProps={{ shrink: true }}
            />
            <LinkPopover
              linkText={t('calc__help__invoiceNumberLink')}
              text={formatCopy(t('calc__help__invoiceNumber'))}
            />
          </div>
          <div className='invoice-field'>
            <DateInput />
            <LinkPopover
              linkText={t('calc__help__invoiceDateLink')}
              text={formatCopy(t('calc__help__invoiceDate'))}
            />
          </div>
          <div className='invoice-field'>
            <IncotermInput/>
            <LinkPopover
              linkText={t('calc__help__invoiceIncotermsLink')}
              text={formatCopy(t('calc__help__invoiceIncoterms'))}
            />
          </div>
          {defaultCityName && defaultCity && <LocationInput defaultCity={defaultCity} />}
          {!defaultCityName && <LocationInput />}
        </div>
        {!currentInvoice && saveButtonIsActive && (<Typography variant='h6' className='invoice__title' sx={{ marginBottom: 3 }}>
          {t('common__text__products')}
        </Typography>)}
        {currentInvoice && (
          <>
            <div className='main-form__buttons'>
              <Button
                variant='outlined'
                sx={{ minWidth: '212px', marginRight: 3}}
                onClick={onClose}
              >{t('actions__text__cancel')}</Button>
              <Button
                variant='contained'
                type='submit'
                {...demoPlayback.saveButtonProps}
                disabled={demoPlayback.saveButtonDisabled}
                startIcon={<CheckIcon />}
                sx={{ minWidth: 404}}
              >{t('actions__text__saveInvoice')}</Button>
            </div>
            {showErrorNotificationInvoice && <Notification type={NOTIFICATION_TYPE.ERROR} title={t('errors__message__save')} text={t('errors__message__tryLater5Min')} sx={{ marginTop: 4 }} /> }
          </>
        )}
        {isFirstInvoice && saveButtonIsActive &&
            <AddForm
              count={1}
              createInvoice={createNewInvoice}
              showErrorNotificationProduct={showErrorNotificationProduct}
              handleSuccess={handleSuccessAddedProduct}
              handleRemove={onCloseInvoiceForm}
            />}
      </form>
    </FormProvider>
  )
}

export default EditInvoice
